import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useAppContext } from '@prisjakt/ui-app-context';
import { useViewportContext } from 'src/components/viewport/context';

import HeaderBlock from 'src/areas/product-collection/components/header-block';
import { SearchResultsNavigation } from '../navigation';
import { NoSearchResults } from 'src/components/no-search-results';
import { ProductResults } from './product-results';
import { DidYouMean } from '../did-you-mean';
import { PageContent } from 'src/components/page';
import { CategoryResults } from './category-results';
import { BrandResults } from './brand-results';
import { ShopResults } from './shop-results';
import { KeywordSuggestions } from '../keyword-suggestions';
import RelevancyFeedback from '../feedback-relevancy';

import {
  trackSearchNoResultViewed,
  trackSearchResultBrandsListed,
  trackSearchResultCategoriesListed,
  trackSearchResultStoresListed,
} from '../../tracking';
import {
  useSearchResultLayoutExperiment,
  SearchResultLayoutExperimentType,
  useTrailingSkyscraperExperiment,
  TrailingSkyscraperExperimentType,
} from 'src/hooks/experiments';

import * as S from './styles';

import { TabAnchor } from '../navigation/types';
import { SearchPageQuery, SearchFacetList } from 'src/types';
import { NEW_SEARCH_INDEX_MARKETS } from '../../constants';
import { useEffectOnMount } from 'src/hooks/useEffectOnMount';

const SHOW_ALL = true;

type Props = {
  data: SearchPageQuery;
};

enum SearchProductFacet {
  CATEGORY_ID = 'category_id',
  BRAND_ID = 'brand_id',
  LOWEST_PRICE = 'lowest_price',
  USER_RATING = 'user_rating',
}

const SearchPage = (props: Props) => {
  const { data } = props;
  const dispatch = useDispatch();
  const { t, market } = useAppContext();
  const { isMobile } = useViewportContext();
  const searchResultLayoutExperiment = useSearchResultLayoutExperiment();
  const [searchResultLayout, setSearchResultLayout] = useState<
    SearchResultLayoutExperimentType
  >(false);
  const [activeTab, setActiveTab] = useState('');
  const location = useLocation();

  const trailingSkyscraperVariant = useTrailingSkyscraperExperiment();
  const [trailingSkyscraper, setTrailingSkyscraper] = useState<
    TrailingSkyscraperExperimentType
  >(false);
  const searchContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTrailingSkyscraper(trailingSkyscraperVariant);
  }, [trailingSkyscraperVariant]);

  useEffect(() => {
    setSearchResultLayout(searchResultLayoutExperiment);
  }, [searchResultLayoutExperiment]);

  useEffect(() => {
    if (activeTab !== location.hash) {
      setActiveTab(location.hash.length === 0 ? TabAnchor.ALL : location.hash);
    }
  }, [location.hash]);

  const getCategoriesFromProductsFacets = () => {
    const productCategoryFacet = data.search?.resources.products.facets?.filter(
      facet => facet?.id === SearchProductFacet.CATEGORY_ID
    ) as SearchFacetList[];
    return productCategoryFacet?.length && productCategoryFacet[0]
      ? productCategoryFacet[0].buckets
      : [];
  };

  useEffectOnMount(() => {
    dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: `search`,
    });
  });

  useEffect(() => {
    const categories = getCategoriesFromProductsFacets();

    // When performing a new search..
    if (data.search?.query?.length) {
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'auto' });

      // Tracking
      if (categories?.length) {
        trackSearchResultCategoriesListed();
      }
      if (data.search?.resources.brands.nodes.length) {
        trackSearchResultBrandsListed();
      }
      if (data.search?.resources.stores.nodes.length) {
        trackSearchResultStoresListed();
      }

      setActiveTab(location.hash.length > 0 ? location.hash : TabAnchor.ALL);
    }
  }, [data.search?.query]);

  const categories = getCategoriesFromProductsFacets();
  const newSearch = NEW_SEARCH_INDEX_MARKETS.includes(market.code);

  if ((newSearch && !data.newSearch) || (!newSearch && !data.search))
    return null;

  const { products, brands, stores } = data.search?.resources || {};
  const { didYouMean, popularSearches, keywordSuggestions: keywords } = data;

  const productsFromNewSearch = data.newSearch?.results?.products;
  const categoriesFromNewSearch = data.newSearch?.results?.categories;
  const brandsFromNewSearch = data.newSearch?.results?.brands;
  const shopsFromNewSearch = data.newSearch?.results?.shops;

  const productsCount = newSearch
    ? productsFromNewSearch?.pageInfo?.total || 0
    : products?.pageInfo?.total || 0;
  const categoriesCount = newSearch
    ? categoriesFromNewSearch?.pageInfo?.total || 0
    : categories?.length || 0;
  const storesCount = newSearch
    ? shopsFromNewSearch?.pageInfo?.total || 0
    : stores?.nodes.length || 0;
  const brandsCount = newSearch
    ? brandsFromNewSearch?.pageInfo?.total || 0
    : brands?.nodes.length || 0;

  const noResults = !newSearch
    ? !products?.nodes?.length &&
      !categories?.length &&
      !brands?.nodes?.length &&
      !stores?.nodes?.length
    : !productsFromNewSearch?.nodes?.length &&
      !categoriesFromNewSearch?.nodes.length &&
      !brandsFromNewSearch?.nodes.length &&
      !shopsFromNewSearch?.nodes?.length;

  const brandNodes = newSearch
    ? brandsFromNewSearch?.nodes || []
    : brands?.nodes || [];

  const searchQuery =
    (data.newSearch && data.newSearch.query) ||
    (data.search && data.search.query) ||
    '';

  const useWidePageContent =
    searchResultLayout === 'wide' ||
    searchResultLayout === 'wide and without panorama';
  const disablePanoramaAd =
    searchResultLayout === 'without panorama' ||
    searchResultLayout === 'wide and without panorama';

  const handleOnClick = (tab: string) => {
    window.scrollTo({
      top: searchContainer.current?.offsetTop || 0,
      behavior: 'auto',
    });

    setActiveTab(tab);
  };

  const handleActiveTab = (currentTab: string) => {
    if (activeTab.length === 0) return SHOW_ALL;
    return activeTab === currentTab;
  };

  return (
    <PageContent
      withAds
      data-test="SearchResultsPage"
      isWide={useWidePageContent}
      trailingSkyscraperVariant={trailingSkyscraper}
      {...(disablePanoramaAd && { topPlacements: [] })}
    >
      <HeaderBlock
        title={t('search', 'search-results.title', {
          query: searchQuery,
        })}
      />
      {didYouMean?.length ? (
        <S.ModuleWrapper>
          <DidYouMean
            queries={didYouMean.map(s => s.text)}
            searchQuery={searchQuery}
          />
        </S.ModuleWrapper>
      ) : null}

      {keywords?.length ? (
        <KeywordSuggestions isMobile={isMobile} keywords={keywords} />
      ) : null}

      {noResults ? (
        <NoSearchResults
          onRender={() => trackSearchNoResultViewed(searchQuery)}
          popularSearches={popularSearches}
          feedbackForm={<RelevancyFeedback query={searchQuery} />}
        />
      ) : (
        <>
          <SearchResultsNavigation
            activeTab={activeTab}
            brandsCount={brandsCount}
            categoriesCount={categoriesCount}
            productsCount={productsCount}
            storesCount={storesCount}
            onClick={handleOnClick}
          />

          <S.SearchResults
            gap={activeTab.length > 0 && activeTab !== TabAnchor.ALL ? 0 : 2}
          >
            <CategoryResults
              active={handleActiveTab(TabAnchor.CATEGORIES)}
              categories={
                newSearch ? categoriesFromNewSearch?.nodes : categories
              }
              count={categoriesCount}
              newSearch={newSearch}
              wideContent={useWidePageContent}
              showAll={
                !!(
                  handleActiveTab(TabAnchor.CATEGORIES) &&
                  activeTab !== TabAnchor.ALL
                )
              }
            />

            <ProductResults
              active={handleActiveTab(TabAnchor.PRODUCTS)}
              facets={newSearch ? data.newSearch?.filters : products?.facets}
              pageInfo={
                newSearch ? productsFromNewSearch?.pageInfo : products?.pageInfo
              }
              products={
                newSearch
                  ? productsFromNewSearch?.nodes || []
                  : products?.nodes || []
              }
              productsCount={productsCount}
              sort={products?.pageInfo?.sort}
              query={searchQuery}
              newSearch={newSearch}
              isWide={useWidePageContent}
            />

            <BrandResults
              active={handleActiveTab(TabAnchor.BRANDS)}
              brands={brandNodes}
              count={brandsCount}
              showAll={
                !!(
                  handleActiveTab(TabAnchor.BRANDS) &&
                  activeTab !== TabAnchor.ALL
                )
              }
            />

            <ShopResults
              active={handleActiveTab(TabAnchor.STORES)}
              shops={
                newSearch
                  ? shopsFromNewSearch?.nodes || []
                  : stores?.nodes || []
              }
              count={storesCount}
              wideContent={useWidePageContent}
              showAll={
                !!(
                  handleActiveTab(TabAnchor.BRANDS) &&
                  activeTab !== TabAnchor.ALL
                )
              }
            />
          </S.SearchResults>
        </>
      )}
    </PageContent>
  );
};

export default SearchPage;
